import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, Topic, Question, ImportReport } from '../api/rest';
import { currentIdToken } from '../auth/cognito';
import { relativeTime } from '../lib/time';
import { useT } from '../i18n';

export function Authoring() {
  const t = useT();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [newTopic, setNewTopic] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'title'>('newest');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) navigate('/admin', { replace: true });
      else void loadTopics();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTopics = async () => {
    try {
      setTopics(await api.listTopics());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim()) return;
    try {
      await api.createTopic(newTopic.trim(), topics.length);
      setNewTopic('');
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add topic');
    }
  };

  const removeTopic = async (id: string) => {
    if (!confirm(t('authoring.deleteTopicConfirm'))) return;
    await api.deleteTopic(id);
    await loadTopics();
  };

  const runImport = async (file: File) => {
    setError(null);
    setImportReport(null);
    try {
      const payload = JSON.parse(await file.text());
      const report = await api.importContent(payload);
      setImportReport(report);
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('authoring.importFailed'));
    }
  };

  const q = search.trim().toLowerCase();
  const visibleTopics = topics
    .filter((t) => t.title.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title);
      // newest first by createdAt (missing dates sort last)
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });

  return (
    <div className="stack">
      <div className="rowbetween">
        <h1>{t('authoring.title')}</h1>
        <Link to="/admin/games" className="ghost">{t('common.back')}</Link>
      </div>
      {error && <div className="banner error">{t(error)}</div>}

      <div className="card">
        <h2>{t('authoring.bulkImport')}</h2>
        <p className="muted">{t('authoring.importHelp')}</p>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void runImport(f);
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
        {importReport && (
          <div className="banner success">
            {t('authoring.imported', { imported: importReport.imported, skipped: importReport.skipped })}
            <ul>
              {importReport.reports.map((r, i) => (
                <li key={i}>{r.imported ? '✅' : '⚠️'} {r.title} {r.error ? `— ${r.error}` : ''}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card">
        <div className="section-head">
          <h2>{t('authoring.topics')} <span className="chip">{topics.length}</span></h2>
        </div>

        <div className="addrow">
          <input
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTopic()}
            placeholder={t('authoring.newTopicPlaceholder')}
          />
          <button className="primary" onClick={addTopic} disabled={!newTopic.trim()}>{t('authoring.add')}</button>
        </div>

        <div className="toolbar">
          <div className="searchbox">
            <span className="searchicon">🔍</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('authoring.filterTopics')} />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'title')}>
            <option value="newest">{t('authoring.newestFirst')}</option>
            <option value="title">{t('authoring.titleAZ')}</option>
          </select>
        </div>

        {visibleTopics.length === 0 ? (
          <p className="muted empty">{topics.length === 0 ? t('authoring.noTopics') : t('authoring.noMatch')}</p>
        ) : (
          <ul className="topiclist">
            {visibleTopics.map((topic) => (
              <TopicEditor key={topic.id} topic={topic} onDelete={() => removeTopic(topic.id)} onError={setError} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TopicEditor({ topic, onDelete, onError }: { topic: Topic; onDelete: () => void; onError: (m: string) => void }) {
  const t = useT();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [points, setPoints] = useState(10);
  const [answer, setAnswer] = useState('');

  const load = async () => {
    try {
      setQuestions(await api.listQuestions(topic.id));
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const addQuestion = async () => {
    if (!text.trim() || points <= 0) return;
    try {
      await api.createQuestion(topic.id, { text: text.trim(), points, answer: answer.trim() });
      setText('');
      setAnswer('');
      setPoints(10);
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const rel = relativeTime(topic.createdAt);

  return (
    <li className={`topicitem ${open ? 'open' : ''}`}>
      <div className="topichead" onClick={toggle}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        <span className="ttitle">{topic.title}</span>
        {open && questions.length > 0 && <span className="chip subtle">{t('authoring.qCount', { n: questions.length })}</span>}
        {rel && <span className="muted small when">{rel}</span>}
        <button className="danger small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>{t('common.delete')}</button>
      </div>
      {open && (
        <div className="questions">
          {questions.map((q) => (
            <QuestionRow key={q.id} q={q} reload={load} onError={onError} />
          ))}
          <div className="qadd">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('authoring.questionText')} />
            <input type="number" value={points} min={1} onChange={(e) => setPoints(Number(e.target.value))} className="pts" />
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t('authoring.answerHostOnly')} />
            <button className="primary small" onClick={addQuestion}>{t('common.add')}</button>
          </div>
        </div>
      )}
    </li>
  );
}

function QuestionRow({ q, reload, onError }: { q: Question; reload: () => Promise<void>; onError: (m: string) => void }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(q.text);
  const [points, setPoints] = useState(q.points);
  const [answer, setAnswer] = useState(q.answer ?? '');

  const startEdit = () => {
    setText(q.text);
    setPoints(q.points);
    setAnswer(q.answer ?? '');
    setEditing(true);
  };

  const save = async () => {
    if (!text.trim() || points <= 0) return;
    try {
      await api.updateQuestion(q.id, { text: text.trim(), points, answer: answer.trim(), order: q.order });
      setEditing(false);
      await reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const remove = async () => {
    if (!confirm(t('authoring.deleteQuestionConfirm'))) return;
    try {
      await api.deleteQuestion(q.id);
      await reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  if (editing) {
    return (
      <div className="qedit">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('authoring.questionText')} />
        <input type="number" min={1} value={points} onChange={(e) => setPoints(Number(e.target.value))} className="pts" />
        <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t('authoring.answerHostOnly')} />
        <button className="primary small" onClick={save}>{t('common.save')}</button>
        <button className="ghost small" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
      </div>
    );
  }

  return (
    <div className="qrow">
      <span>{q.order}. {q.text} <span className="points">({q.points})</span>{q.answer && <span className="muted small"> — {q.answer}</span>}</span>
      <span className="row">
        <button className="ghost small" onClick={startEdit}>{t('common.edit')}</button>
        <button className="danger small" onClick={remove}>✗</button>
      </span>
    </div>
  );
}
